import {call, put, select} from "redux-saga/effects";
import {fetchLevelMap} from "../actions";
import {IApplicationState} from "../../index";
import {ILevelGameState, TileType} from "../types";
import {ILevelMapApiResponse, ILevelMapRow, ILevelMapWithQuestions} from "./types";
import preloadImages from "./preloadImages";
import apiCall from "../../../shared/utils/apiCall";
import {mapQuestions} from "./handleFetchQuestions";
import {trackError} from "../../log/actions";
import {LogLevel} from "../../log/types";
import {makeTileId} from "../../../shared/utils/parseTileId";

function rebuildTilesMap(levelId: number, tileRows: ILevelMapRow[], questionIds: number[]) {
    const rows = tileRows.length;
    const columns = tileRows[0].tiles.length;
    // empty tile matrix[rows x columns]
    const tiles = (Array as any).from(Array(rows), () => (Array as any).from(Array(columns), () => {
        return {};
    }));

    let lastQuestionIndex = 0;
    let revealedQuestions = [];
    let exitTileId;
    let tileCount = 0;

    for (let row = 0; row < rows; row++) {
        for (let column = 0; column < columns; column++) {
            const tile = tileRows[row].tiles[column];
            const questionCount = tile.tileType === TileType.SimpleQuestion || tile.tileType === TileType.Exit
                ? Math.max(1, tile.questionCount || 1)
                : 0;
            const tileQuestions = questionCount > 0
                ? questionIds.slice(lastQuestionIndex, lastQuestionIndex + questionCount)
                : [];

            lastQuestionIndex = lastQuestionIndex + questionCount;

            const reverseRow = rows - row - 1;
            const tileId = makeTileId(levelId, reverseRow, column);

            tiles[reverseRow][column] = tile && tile.tileType
                ? {
                    id: tileId,
                    levelId: levelId,
                    tileType: tile.tileType,
                    questionCount: questionCount,
                    isRevealed: !!tile.revealed,
                    isBonusQuestion: !!tile.isBonusQuestion,
                    questions: tileQuestions,
                    coords: {
                        x: column,
                        y: reverseRow
                    }
                }
                : {};

            if (tile.revealed) {
                revealedQuestions.push(tileQuestions);
            }

            if (tile.tileType === TileType.Exit) {
                exitTileId = tileId;
            }

            if (tile.tileType) {
                tileCount++;
            }
        }
    }

    return {
        tiles,
        revealedQuestions,
        exitTileId,
        tileCount
    };
}

function normalizeData(levelMaps: ILevelMapWithQuestions[]): ILevelGameState {
    const result = {
        levelMaps: {} as Record<number, any>,
        questions: {},
        answers: {},
        authors: {},
        unusedQuestions: []
    };

    for (let levelMap of levelMaps) {
        const {questions, answers, authors, questionIds} = mapQuestions(levelMap.questions);
        const {tiles, revealedQuestions, exitTileId, tileCount} = rebuildTilesMap(levelMap.levelId, levelMap.tileRows, questionIds);

        result.levelMaps[levelMap.levelId] = {
            levelId: levelMap.levelId,
            exitTileId: exitTileId,
            tileCount: tileCount,
            decals: levelMap.decals,
            tiles: tiles
        };

        result.questions = {...result.questions, ...questions};
        result.answers = {...result.answers, ...answers};
        result.authors = {...result.authors, ...authors};

        if (revealedQuestions.length) {
            const imageUrls = revealedQuestions.flatMap(questionIds => {
                return questionIds.map(questionId => questions[questionId].imageUrl);
            });

            preloadImages(imageUrls);
        }
    }

    return result;
}

export default function* handleFetchLevelMaps() {
    try {
        const {existingLevelMap, levelId} = yield select(({game, player}: IApplicationState) => ({
            existingLevelMap: game.levelMaps[player.levelsCompleted + 1],
            levelId: player.levelsCompleted + 1,
        }));

        if (existingLevelMap) {
            return;
        }

        yield put(fetchLevelMap.request());

        const response: ILevelMapApiResponse = yield call(apiCall, {
            params: {
                section: "maps",
                method: "get",
                levelIds: levelId
            }
        });

        if (response.notEnoughQuestions) {
            yield put(trackError({
                logLevel: LogLevel.Warn,
                category: "FetchLevelMaps",
                message: "Not enough questions",
            }));

            yield put(fetchLevelMap.failure({
                message: "Not enough questions"
            }));

            return;
        }

        const levelMaps = normalizeData(response.data);

        yield put(fetchLevelMap.success(levelMaps));
    } catch (error) {
        yield put(trackError({
            logLevel: LogLevel.Fatal,
            category: "FetchLevelMaps",
            message: "Failed to fetch level maps",
            error: error,
        }));

        yield put(fetchLevelMap.failure({
            message: error
        }));
    }
}